import { useState, useEffect, useCallback } from 'react';
import { toast } from 'sonner';
import { 
  AlertTriangle, 
  Phone, 
  MapPin, 
  Mail,
  X,
  Loader2,
  User,
  Star
} from 'lucide-react';
import { Button } from './ui/button';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const SOSButton = ({ token, famigliaId, nomeAssistente, nomeAssistito }) => {
  const [showModal, setShowModal] = useState(false);
  const [sending, setSending] = useState(false);
  const [specialisti, setSpecialisti] = useState([]);
  const [loadingSpecialisti, setLoadingSpecialisti] = useState(false);
  const [sosSent, setSosSent] = useState(false);
  
  // Load specialisti when modal opens
  const loadSpecialisti = useCallback(async () => {
    if (!token) return;
    setLoadingSpecialisti(true);
    try {
      const res = await fetch(`${API_URL}/api/visite/app/specialisti/${token}`);
      if (res.ok) {
        const data = await res.json();
        setSpecialisti(data.specialisti || []);
      }
    } catch (err) {
      console.error('Error loading specialisti:', err);
    } finally {
      setLoadingSpecialisti(false);
    }
  }, [token]);
  
  useEffect(() => {
    if (showModal) {
      loadSpecialisti();
    }
  }, [showModal, loadSpecialisti]);
  
  // Send SOS
  const handleSOS = async () => {
    if (sending || !famigliaId) return;
    
    setSending(true);
    try {
      const res = await fetch(
        `${API_URL}/api/visite/sos/${famigliaId}?assistente_nome=${encodeURIComponent(nomeAssistente || 'Assistente')}`,
        { method: 'POST' }
      );
      
      if (res.ok) {
        const data = await res.json();
        setSosSent(true);
        setSpecialisti(data.specialisti || specialisti);
        
        // Vibra forte per conferma
        if ('vibrate' in navigator) {
          navigator.vibrate([200, 100, 200, 100, 200]);
        }
        
        toast.success('SOS inviato! La famiglia è stata avvisata.', {
          duration: 5000,
          icon: '🚨'
        });
      } else {
        throw new Error('Errore invio SOS');
      }
    } catch (err) {
      toast.error('Errore nell\'invio SOS. Riprova.');
    } finally {
      setSending(false);
    }
  };
  
  // Render specialista card
  const renderSpecialista = (spec) => (
    <div 
      key={spec.id} 
      className={`bg-white rounded-xl p-4 border-2 ${spec.is_primary ? 'border-red-400' : 'border-gray-200'}`}
    >
      <div className="flex items-start gap-3">
        <div className={`w-12 h-12 rounded-full flex items-center justify-center ${
          spec.is_primary ? 'bg-red-100' : 'bg-gray-100'
        }`}>
          <User className={`w-6 h-6 ${spec.is_primary ? 'text-red-600' : 'text-gray-600'}`} />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <p className="font-bold text-gray-900 truncate">{spec.nome}</p>
            {spec.is_primary && (
              <Star className="w-4 h-4 text-yellow-500 fill-yellow-500 flex-shrink-0" />
            )}
          </div>
          <p className="text-sm text-gray-500">{spec.ruolo}</p>
        </div>
      </div>
      
      <div className="mt-3 space-y-2">
        {/* Telefono - cliccabile */}
        {spec.telefono && (
          <a 
            href={`tel:${spec.telefono}`}
            className="flex items-center gap-3 p-3 bg-green-50 rounded-lg hover:bg-green-100 active:scale-[0.98] transition-all"
          >
            <Phone className="w-5 h-5 text-green-600" />
            <span className="font-semibold text-green-700">{spec.telefono}</span>
          </a>
        )}
        
        {/* Telefono emergenza */}
        {spec.telefono_emergenza && (
          <a 
            href={`tel:${spec.telefono_emergenza}`}
            className="flex items-center gap-3 p-3 bg-red-50 rounded-lg hover:bg-red-100 active:scale-[0.98] transition-all"
          >
            <AlertTriangle className="w-5 h-5 text-red-600" />
            <span className="font-semibold text-red-700">{spec.telefono_emergenza}</span>
            <span className="text-xs text-red-500 ml-auto">Emergenza</span>
          </a>
        )}
        
        {/* Indirizzo - link a Maps */}
        {spec.indirizzo && (
          <a 
            href={`https://maps.google.com/?q=${encodeURIComponent(spec.indirizzo + (spec.citta ? ', ' + spec.citta : ''))}`}
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center gap-3 p-3 bg-blue-50 rounded-lg hover:bg-blue-100 active:scale-[0.98] transition-all"
          >
            <MapPin className="w-5 h-5 text-blue-600" />
            <span className="text-sm text-blue-700 truncate">
              {spec.indirizzo}{spec.citta ? `, ${spec.citta}` : ''}
            </span>
          </a>
        )}
        
        {/* Email */}
        {spec.email && (
          <a 
            href={`mailto:${spec.email}`}
            className="flex items-center gap-3 p-3 bg-purple-50 rounded-lg hover:bg-purple-100 active:scale-[0.98] transition-all"
          >
            <Mail className="w-5 h-5 text-purple-600" />
            <span className="text-sm text-purple-700 truncate">{spec.email}</span>
          </a>
        )}
      </div>
    </div>
  );
  
  return (
    <>
      {/* SOS Button - Fixed at BOTTOM CENTER */}
      <button
        onClick={() => setShowModal(true)}
        className="fixed bottom-6 left-1/2 -translate-x-1/2 z-30 w-16 h-16 bg-red-500 hover:bg-red-600 rounded-full shadow-2xl flex items-center justify-center active:scale-95 transition-all animate-pulse"
        data-testid="btn-sos"
      >
        <AlertTriangle className="w-8 h-8 text-white" />
      </button>
      
      {/* Modal */}
      {showModal && (
        <div className="fixed inset-0 z-50 bg-black/70 flex items-end sm:items-center justify-center p-0 sm:p-4">
          <div className="bg-white w-full sm:max-w-md sm:rounded-2xl rounded-t-3xl max-h-[90vh] overflow-hidden flex flex-col animate-in slide-in-from-bottom duration-300">
            {/* Header */}
            <div className="bg-red-500 p-4 flex items-center justify-between sticky top-0">
              <div className="flex items-center gap-3">
                <AlertTriangle className="w-7 h-7 text-white" />
                <div>
                  <h2 className="text-lg font-bold text-white">SOS Emergenza</h2>
                  <p className="text-red-100 text-sm">Assistenza per {nomeAssistito}</p>
                </div>
              </div>
              <button 
                onClick={() => {
                  setShowModal(false);
                  setSosSent(false);
                }}
                className="w-10 h-10 rounded-full bg-white/20 flex items-center justify-center hover:bg-white/30"
              >
                <X className="w-5 h-5 text-white" />
              </button>
            </div>
            
            {/* Content */}
            <div className="flex-1 overflow-y-auto p-4 space-y-4">
              {/* SOS Button */}
              {!sosSent ? (
                <Button
                  onClick={handleSOS}
                  disabled={sending}
                  className="w-full h-20 bg-red-600 hover:bg-red-700 text-white text-xl font-bold rounded-2xl shadow-lg"
                >
                  {sending ? (
                    <Loader2 className="w-8 h-8 animate-spin" />
                  ) : (
                    <>
                      <AlertTriangle className="w-8 h-8 mr-3" />
                      INVIA SOS ALLA FAMIGLIA
                    </>
                  )}
                </Button>
              ) : (
                <div className="bg-green-100 border-2 border-green-400 rounded-2xl p-4 text-center">
                  <div className="w-16 h-16 bg-green-500 rounded-full flex items-center justify-center mx-auto mb-3">
                    <svg className="w-10 h-10 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                    </svg>
                  </div>
                  <p className="font-bold text-green-800 text-lg">SOS Inviato!</p>
                  <p className="text-green-600 text-sm mt-1">La famiglia è stata notificata</p>
                </div>
              )}
              
              {/* Specialisti List */}
              <div className="pt-2">
                <h3 className="font-bold text-gray-900 mb-3 flex items-center gap-2">
                  <Phone className="w-5 h-5 text-gray-600" />
                  Contatti Rapidi
                </h3>
                
                {loadingSpecialisti ? (
                  <div className="text-center py-8">
                    <Loader2 className="w-8 h-8 animate-spin text-gray-400 mx-auto" />
                    <p className="text-gray-500 mt-2">Caricamento contatti...</p>
                  </div>
                ) : specialisti.length === 0 ? (
                  <div className="text-center py-8 bg-gray-50 rounded-xl">
                    <User className="w-12 h-12 text-gray-300 mx-auto mb-2" />
                    <p className="text-gray-500">Nessun contatto salvato</p>
                    <p className="text-sm text-gray-400">La famiglia può aggiungere specialisti dalla dashboard</p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {/* Prima i primary */}
                    {specialisti.filter(s => s.is_primary).map(renderSpecialista)}
                    {specialisti.filter(s => !s.is_primary).map(renderSpecialista)}
                  </div>
                )}
              </div>
            </div>
            
            {/* Footer */}
            <div className="p-4 border-t bg-gray-50">
              <Button
                variant="outline"
                onClick={() => {
                  setShowModal(false);
                  setSosSent(false);
                }}
                className="w-full"
              >
                Chiudi
              </Button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default SOSButton;
